// frontend/src/lib/auth.jsx

import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { Amplify } from "aws-amplify";
import { signOut as amplifySignOut, fetchAuthSession } from "aws-amplify/auth";
import { pushRedditConversionEvent } from "../utils/redditTracking";

/**
 * ---------- JWT helpers ----------
 * Cognito JWT payload is base64url encoded. atob() expects base64,
 * so we normalize + pad before decoding.
 */
function base64UrlToBase64(input) {
  if (!input) return "";
  let s = input.replace(/-/g, "+").replace(/_/g, "/");
  const pad = s.length % 4;
  if (pad) s += "=".repeat(4 - pad);
  return s;
}

function decodeToken(token) {
  try {
    const parts = (token || "").split(".");
    if (parts.length !== 3) return null;
    const payload = parts[1];
    const decodedJson = atob(base64UrlToBase64(payload));
    return JSON.parse(decodedJson);
  } catch (error) {
    console.error("[Auth] Failed to decode token:", error);
    return null;
  }
}

function deriveDisplayNameFromEmail(email) {
  const safe = (email || "").trim();
  const local = safe.split("@")[0] || "";
  if (!local) return null;

  const parts = local
    .replace(/[0-9]+/g, "")
    .split(/[^a-zA-Z]+/)
    .filter(Boolean);

  if (!parts.length) return null;

  const first = parts[0];
  return first.charAt(0).toUpperCase() + first.slice(1).toLowerCase();
}

function pickBestName(decoded, fallbackEmail) {
  const name =
    decoded?.name ||
    decoded?.given_name ||
    decoded?.preferred_username ||
    decoded?.["cognito:username"] ||
    null;

  if (name && String(name).trim()) return String(name).trim();

  const email = decoded?.email || fallbackEmail || null;
  const derived = deriveDisplayNameFromEmail(email);
  return derived || null;
}

function persistUserProfile({ email, name, picture }) {
  try {
    const payload = { email, name, picture, updatedAt: Date.now() };
    localStorage.setItem("snapgrad_user", JSON.stringify(payload));
    localStorage.setItem("userData", JSON.stringify(payload)); // optional compat
  } catch {}
}

function readPersistedUserProfile() {
  try {
    const raw =
      localStorage.getItem("snapgrad_user") || localStorage.getItem("userData");
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

const AuthContext = createContext(undefined);

const requiredEnv = {
  region: process.env.REACT_APP_COGNITO_REGION,
  userPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
  clientId: process.env.REACT_APP_COGNITO_CLIENT_ID,
  domain: process.env.REACT_APP_COGNITO_DOMAIN,
  redirectUri: process.env.REACT_APP_COGNITO_REDIRECT_URI,
  logoutUri: process.env.REACT_APP_COGNITO_LOGOUT_URI,
};

// Helper function to ensure domain is properly formatted
const getFormattedDomain = (domain, region) => {
  if (!domain) return "";
  if (domain.endsWith("amazoncognito.com")) return domain;
  if (domain.includes(".")) return domain;
  return `${domain}.auth.${region}.amazoncognito.com`;
};

const formattedDomain = getFormattedDomain(requiredEnv.domain, requiredEnv.region);

const missing = Object.entries(requiredEnv)
  .filter(([, value]) => !value)
  .map(([key]) => key);

if (missing.length) {
  console.warn(
    `[Auth] Missing Cognito env vars: ${missing.join(", ")}. Auth will not work.`
  );
}

const authConfig = {
  Auth: {
    Cognito: {
      region: requiredEnv.region,
      userPoolId: requiredEnv.userPoolId,
      userPoolClientId: requiredEnv.clientId,
      oauth: {
        domain: formattedDomain,
        scopes: ["openid", "email", "profile"],
        redirectSignIn: [requiredEnv.redirectUri],
        redirectSignOut: [requiredEnv.logoutUri],
        responseType: "code",
      },
    },
  },
};

if (Object.values(requiredEnv).every(Boolean)) {
  Amplify.configure(authConfig);
}

/**
 * Build Hosted UI authorize URL with:
 * - prompt
 * - login_hint (email)
 * - identity_provider=Google (only when doing Google OAuth)
 */
function buildAuthorizeUrl({ identityProvider, email, prompt } = {}) {
  const redirectUri = requiredEnv.redirectUri;
  const clientId = requiredEnv.clientId;
  const domain = formattedDomain;

  const params = new URLSearchParams();
  params.set("client_id", clientId);
  params.set("response_type", "code");
  params.set("scope", "email openid profile");
  params.set("redirect_uri", redirectUri);

  try {
    // keep state so you can return to the same page after login
    params.set("state", window.location.pathname || "/");
  } catch {}

  if (prompt) params.set("prompt", prompt);
  if (email) params.set("login_hint", email);
  if (identityProvider) params.set("identity_provider", identityProvider);

  return `https://${domain}/oauth2/authorize?${params.toString()}`;
}

/**
 * Build Hosted UI logout URL
 */
function buildHostedLogoutUrl() {
  const clientId = requiredEnv.clientId;
  const domain = formattedDomain;
  const logoutUri = requiredEnv.logoutUri;

  const params = new URLSearchParams();
  params.set("client_id", clientId);
  params.set("logout_uri", logoutUri);

  return `https://${domain}/logout?${params.toString()}`;
}

export function AuthProvider({ children }) {
  const [loading, setLoading] = useState(true);
  const [isSignedIn, setIsSignedIn] = useState(false);
  const [userEmail, setUserEmail] = useState(null);
  const [userName, setUserName] = useState(null);
  const [userPicture, setUserPicture] = useState(null);
  const [idToken, setIdToken] = useState(null);
  const [accessToken, setAccessToken] = useState(null);

  const refreshSession = async () => {
    try {
      setLoading(true);

      let session = await fetchAuthSession();

      // Fallback to localStorage (if something stored LastAuthResult)
      if (!session?.tokens?.idToken) {
        const clientId = requiredEnv.clientId;
        const lastAuthResultKey = `CognitoIdentityServiceProvider.${clientId}.LastAuthResult`;
        const storedAuthResult = localStorage.getItem(lastAuthResultKey);

        if (storedAuthResult) {
          try {
            const authResult = JSON.parse(storedAuthResult);
            session = {
              tokens: {
                idToken: { toString: () => authResult.idToken },
                accessToken: { toString: () => authResult.accessToken },
              },
            };
          } catch (e) {
            console.error("[Auth] Failed to parse stored auth result:", e);
          }
        }
      }

      if (session?.tokens?.idToken) {
        const idTokenStr = session.tokens.idToken.toString();
        const accessTokenStr = session.tokens.accessToken?.toString() ?? null;

        setIdToken(idTokenStr);
        setAccessToken(accessTokenStr);

        const decoded = decodeToken(idTokenStr);
        const persisted = readPersistedUserProfile();

        if (decoded) {
          const email = decoded.email || persisted?.email || null;
          const name = pickBestName(decoded, email) || persisted?.name || null;
          const picture = decoded.picture || persisted?.picture || null;

          setUserEmail(email);
          setUserName(name);
          setUserPicture(picture);
          setIsSignedIn(true);

          persistUserProfile({ email, name, picture });
        } else if (persisted?.email) {
          setUserEmail(persisted.email || null);
          setUserName(
            persisted.name ||
              deriveDisplayNameFromEmail(persisted.email) ||
              null
          );
          setUserPicture(persisted.picture || null);
          setIsSignedIn(true);
        } else {
          setIsSignedIn(false);
          setUserEmail(null);
          setUserName(null);
          setUserPicture(null);
          setIdToken(null);
          setAccessToken(null);
        }
      } else {
        setIsSignedIn(false);
        setUserEmail(null);
        setUserName(null);
        setUserPicture(null);
        setIdToken(null);
        setAccessToken(null);
      }
    } catch (e) {
      console.error("[Auth] refreshSession error:", e);
      setIsSignedIn(false);
      setUserEmail(null);
      setUserName(null);
      setUserPicture(null);
      setIdToken(null);
      setAccessToken(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshSession();
  }, []);

  /**
   * SIGN IN
   * - "Google" => Google OAuth via Hosted UI
   * - "Email"  => Cognito Hosted UI (Passwordless Email OTP happens there)
   *
   * NOTE: For Email OTP, do NOT pass identity_provider.
   * Cognito will show the OTP email flow if you enabled Passwordless Email OTP in the console.
   */
  const signIn = async (provider = "Email", email = "") => {
    const cleanedEmail = (email || "").trim().toLowerCase();

    if (provider === "Google") {
      pushRedditConversionEvent({
        event: "sign_up_google",
        userData: cleanedEmail ? { email_address: cleanedEmail } : undefined,
        eventData: {
          action: "signup",
          source: "google_oauth",
        },
      });
      const url = buildAuthorizeUrl({
        identityProvider: "Google",
        email: cleanedEmail,
        prompt: "select_account",
      });
      setTimeout(() => {
        window.location.href = url;
      }, 150);
      return;
    }

    // Email OTP hosted UI
    const url = buildAuthorizeUrl({
      email: cleanedEmail,
      prompt: "login",
    });
    window.location.href = url;
  };

  /**
   * SIGN OUT
   */
  const signOut = async () => {
    try {
      await amplifySignOut();
    } catch (error) {
      console.warn("Sign out error:", error);
    }

    try {
      const clientId = requiredEnv.clientId;

      const keysToRemove = [
        `CognitoIdentityServiceProvider.${clientId}.LastAuthResult`,
        `CognitoIdentityServiceProvider.${clientId}.accessToken`,
        `CognitoIdentityServiceProvider.${clientId}.idToken`,
        `CognitoIdentityServiceProvider.${clientId}.refreshToken`,
        `aws.amplify.${clientId}.metadata`,
        "snapgrad_user",
        "userData",
      ];

      keysToRemove.forEach((key) => localStorage.removeItem(key));

      Object.keys(localStorage).forEach((key) => {
        if (
          key.includes("CognitoIdentityServiceProvider") ||
          key.includes("aws.amplify")
        ) {
          localStorage.removeItem(key);
        }
      });
    } catch (e) {
      console.error("[Auth] Error clearing localStorage:", e);
    }

    setIsSignedIn(false);
    setUserEmail(null);
    setUserName(null);
    setUserPicture(null);
    setIdToken(null);
    setAccessToken(null);

    if (Object.values(requiredEnv).every(Boolean)) {
      window.location.href = buildHostedLogoutUrl();
    }
  };

  const getToken = async () => {
    try {
      const session = await fetchAuthSession();
      const token = session?.tokens?.idToken?.toString() ?? null;
      if (token) setIdToken(token);
      return token;
    } catch (error) {
      console.error("Failed to get token:", error);
      return null;
    }
  };

  const value = useMemo(
    () => ({
      isSignedIn,
      loading,
      userEmail,
      userName,
      userPicture,
      signIn,
      signOut,
      getToken,
      refreshSession,
      idToken,
      accessToken,
    }),
    [isSignedIn, loading, userEmail, userName, userPicture, idToken, accessToken]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}
